// One saved gallery for the job: authenticated uploads and reads use the current
// installation/company. Reports carry server-issued upload receipts, never raw URLs.
import { useRef, useState, useEffect } from "react";
import { Camera, Image as ImageIcon } from "lucide-react";
import { Button, cn } from "@aiw/ui";
import { useT } from "../i18n";
import {authHeaders,fetchImpl} from "../lib/api";
import {env} from "../lib/env";
import {useJobInstalls} from "../hooks/useReads";
import {useQueryClient} from "@tanstack/react-query";
import { time } from "../lib/format";
import { useReportActions, type Photo } from "../state/reports";
import { preparePhoto } from "../lib/photo-compress";

export function PhotoButtons({ jobRef, label, size = "sm" }: { jobRef: string; label?: string; size?: "sm" | "touch" }) {
  const { t } = useT();
  const {assertCurrent}=useReportActions();
  const installs=useJobInstalls(jobRef),installation=installs.data?.[0]?.id;
  const cache=useQueryClient();
  const cam = useRef<HTMLInputElement>(null);
  const lib = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onFiles(files: FileList | null) {
    if (!files?.length||!installation) return;
    setBusy(true); setError(null);
    try {
      for (const f of Array.from(files)) {
        assertCurrent();
        const photo=await preparePhoto(f);
        const form=new FormData();form.set("file",photo);if(label)form.set("label",label);
        const response=await fetchImpl(`${env.apiBase}/api/installer-photos/${encodeURIComponent(jobRef)}/${encodeURIComponent(installation)}`,{method:"POST",headers:await authHeaders(),body:form});
        if(!response.ok)throw Error(response.status===413?"size":response.status===401||response.status===403?"access":response.status===415?"format":"upload");
        await cache.invalidateQueries({queryKey:["installer-photos",jobRef]});
      }
    } catch (cause) {
      const reason=cause instanceof Error?cause.message:"upload";
      setError(t(reason==="decode"||reason==="encode"?"photo_prepare_failed":reason==="size"?"photo_too_large":reason==="format"?"photo_format_failed":reason==="access"?"photo_access_failed":"photo_upload_failed"));
    } finally {
      setBusy(false);
      if (cam.current) cam.current.value = "";
      if (lib.current) lib.current.value = "";
    }
  }

  const h = size === "touch" ? "h-11" : "h-9";
  return (
    <span className="flex flex-wrap items-center gap-2">
      <input ref={cam} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => void onFiles(e.target.files)} aria-hidden />
      <input ref={lib} type="file" accept="image/*" multiple className="hidden" onChange={(e) => void onFiles(e.target.files)} aria-hidden />
      <Button type="button" size={size === "touch" ? "touch" : "sm"} variant="outline" className={cn(h, "min-w-[44px]")} disabled={busy||!installation} onClick={() => cam.current?.click()}><Camera aria-hidden />{busy ? t("uploading") : t("camera")}</Button>
      <Button type="button" size={size === "touch" ? "touch" : "sm"} variant="outline" className={cn(h, "min-w-[44px]")} disabled={busy||!installation} onClick={() => lib.current?.click()}><ImageIcon aria-hidden />{t("upload")}</Button>
      {error && <span className="w-full text-micro text-destructive" role="alert">{error}</span>}
    </span>
  );
}

export function PhotoGrid({ photos, cols = 3, className }: { photos: Photo[]; cols?: 2 | 3 | 4; className?: string }) {
  const { t, lang } = useT();
  if (!photos.length) return <p className="text-sm text-muted-foreground">{t("no_photos")}</p>;
  return (
    <ul className={cn("grid gap-2", cols === 2 ? "grid-cols-2" : cols === 4 ? "grid-cols-3 md:grid-cols-4" : "grid-cols-3", className)}>
      {photos.map((p) => (
        <li key={p.path} className="relative aspect-square overflow-hidden rounded-md border bg-muted">
          <PrivatePhoto photo={p} />
          <span className="absolute inset-x-1 bottom-1 truncate rounded bg-background/85 px-1.5 font-mono text-micro leading-5">{time(p.at, lang)}{p.label ? ` · ${p.label === "set-out" ? t("setout_title") : p.label}` : ""}</span>
        </li>
      ))}
    </ul>
  );
}

function PrivatePhoto({photo}:{photo:Photo}){
 const {t}=useT();const [url,setUrl]=useState<string|null>(null),[failed,setFailed]=useState(false);
 useEffect(()=>{const abort=new AbortController();let objectUrl:string|null=null;setUrl(null);setFailed(false);
  void(async()=>{try{
    if(!photo.url.startsWith('/api/installer-photos/'))throw Error('Unverified photo link');
    const response=await fetchImpl(`${env.apiBase}${photo.url}`,{headers:await authHeaders(),signal:abort.signal});if(!response.ok)throw Error('Photo unavailable');
    const blob=await response.blob();if(abort.signal.aborted)return;objectUrl=URL.createObjectURL(blob);setUrl(objectUrl);
  }catch{if(!abort.signal.aborted)setFailed(true);}})();
  return()=>{abort.abort();if(objectUrl)URL.revokeObjectURL(objectUrl);};
 },[photo.url]);
 return url?<img src={url} onError={()=>{setUrl(null);setFailed(true);}} alt={photo.label??photo.name} className="h-full w-full object-cover" loading="lazy"/>:failed?<span role="alert" className="text-micro">{t('photos_load_failed')}</span>:<span aria-busy="true"/>;
}
